import type { ModulationSnapshot, PadSnapshot, RenderMode, VideoParameters } from "./types";
import type { SourceFrame } from "./sources";

const VERTEX_SHADER = `#version 300 es
precision highp float;
out vec2 v_uv;
void main() {
  vec2 p = vec2((gl_VertexID << 1) & 2, gl_VertexID & 2);
  v_uv = p;
  gl_Position = vec4(p * 2.0 - 1.0, 0.0, 1.0);
}`;

const EFFECT_SHADER = `#version 300 es
precision highp float;
precision highp int;
in vec2 v_uv;
out vec4 out_color;
uniform sampler2D u_source;
uniform sampler2D u_feedback;
uniform vec2 u_sourceSize;
uniform vec2 u_outputSize;
uniform float u_time;
uniform float u_frame;
uniform int u_fit;
uniform int u_composite;
uniform float u_horizontalTear;
uniform float u_verticalRoll;
uniform float u_lineShift;
uniform float u_dropout;
uniform float u_chromaDisplacement;
uniform float u_chromaPhase;
uniform float u_chromaGain;
uniform float u_desaturation;
uniform float u_snow;
uniform float u_impulses;
uniform float u_brightnessBars;
uniform float u_clipping;
uniform float u_solarize;
uniform float u_audioReactive;
uniform float u_feedbackAmount;
uniform float u_syncDistortion;
uniform float u_burstDistortion;
uniform float u_burstGain;
uniform float u_linePosition;
uniform float u_lineWidth;
uniform float u_gain;
uniform float u_dcOffset;
uniform float u_audioRms;
uniform float u_audioPeak;
uniform float u_syncKick;
uniform float u_burstKill;
uniform float u_lineCrush;
uniform float u_frameTear;
uniform float u_whiteout;
uniform float u_unlock;

float hash12(vec2 p) {
  vec3 p3 = fract(vec3(p.xyx) * 0.1031);
  p3 += dot(p3, p3.yzx + 33.33);
  return fract((p3.x + p3.y) * p3.z);
}

vec2 sourceUv(vec2 uv) {
  float sourceAspect = u_sourceSize.x / max(1.0, u_sourceSize.y);
  float outputAspect = u_outputSize.x / max(1.0, u_outputSize.y);
  vec2 p = uv - 0.5;
  if (u_fit == 1) {
    if (sourceAspect > outputAspect) p.y *= sourceAspect / outputAspect;
    else p.x *= outputAspect / sourceAspect;
  } else {
    if (sourceAspect > outputAspect) p.x *= outputAspect / sourceAspect;
    else p.y *= sourceAspect / outputAspect;
  }
  return p + 0.5;
}

vec3 readSource(vec2 uv) {
  vec2 mapped = sourceUv(uv);
  if (any(lessThan(mapped, vec2(0.0))) || any(greaterThan(mapped, vec2(1.0)))) return vec3(0.0);
  return texture(u_source, mapped).rgb;
}

vec3 rgbToYiq(vec3 rgb) {
  return vec3(
    dot(rgb, vec3(0.299, 0.587, 0.114)),
    dot(rgb, vec3(0.596, -0.274, -0.322)),
    dot(rgb, vec3(0.211, -0.523, 0.312))
  );
}

vec3 yiqToRgb(vec3 yiq) {
  return vec3(
    yiq.x + 0.956 * yiq.y + 0.621 * yiq.z,
    yiq.x - 0.272 * yiq.y - 0.647 * yiq.z,
    yiq.x - 1.106 * yiq.y + 1.703 * yiq.z
  );
}

void main() {
  vec2 uv = v_uv;
  float line = floor(uv.y * u_outputSize.y);
  float lineNoise = hash12(vec2(line, floor(u_frame * 0.5)));
  float unlocked = u_unlock * (hash12(vec2(floor(u_time * 9.0), line * 0.01)) - 0.5);
  uv.y = fract(uv.y + u_verticalRoll * u_time * 0.08 + unlocked * 0.08);
  float jitter = (lineNoise - 0.5) * u_lineShift * u_lineShift * 0.15;
  float tearCenter = fract(0.21 + u_time * 0.071);
  float tearBand = smoothstep(0.15, 0.0, abs(uv.y - tearCenter));
  jitter += tearBand * (u_horizontalTear + u_frameTear) * sin(uv.y * 91.0 + u_time * 7.0) * 0.18;
  jitter += (u_syncKick + u_syncDistortion) * 0.13 * exp(-abs(uv.y - fract(u_time * 0.8)) * 32.0);
  uv.x = fract(uv.x + jitter);

  if (u_lineCrush > 0.001) {
    float crushCenter = fract(0.4 + u_time * 0.19);
    float crushed = smoothstep(0.09, 0.0, abs(uv.y - crushCenter)) * u_lineCrush;
    uv.x = mix(uv.x, 0.5, crushed);
  }

  float chromaPx = (u_chromaDisplacement + u_audioReactive * u_audioRms * 0.08) / u_outputSize.x * 42.0;
  vec3 feedback = texture(u_feedback, v_uv).rgb;
  vec3 base = readSource(uv);
  // In composite mode the prior decoded frame is fed back before the new
  // YIQ encoding pass. Image-space mode retains the post-effect blend below.
  if (u_composite == 1) base = mix(base, feedback, clamp(u_feedbackAmount, 0.0, 0.975));
  vec3 yiq = rgbToYiq(base);
  vec3 shifted = rgbToYiq(readSource(vec2(fract(uv.x + chromaPx), uv.y)));
  yiq.yz = shifted.yz;
  float phase = u_chromaPhase * 3.14159265 + u_burstDistortion * sin(u_time * 4.7) + u_unlock * sin(u_time * 17.0);
  mat2 rotation = mat2(cos(phase), -sin(phase), sin(phase), cos(phase));
  yiq.yz = rotation * yiq.yz;
  yiq.yz *= u_chromaGain * u_burstGain * (1.0 - u_desaturation) * (1.0 - u_burstKill);

  if (u_composite == 1) {
    float carrier = sin((uv.x * 227.5 + line * 0.5) * 6.2831853 + phase);
    yiq.x += carrier * length(yiq.yz) * (0.035 + u_burstDistortion * 0.12);
    yiq.yz *= 1.0 - u_burstDistortion * 0.8;
  }

  float randomValue = hash12(vec2(gl_FragCoord.xy) + vec2(u_frame * 17.0, u_frame * 3.0));
  float snow = (randomValue - 0.5) * u_snow * (0.65 + u_audioPeak);
  float impulse = step(0.997 - u_impulses * 0.02, randomValue) * (hash12(vec2(line, u_frame)) - 0.5);
  float dropout = step(1.0 - u_dropout * 0.06, hash12(vec2(line, floor(u_frame / 3.0))));
  yiq.x += snow + impulse * 1.6;
  yiq = mix(yiq, vec3(hash12(vec2(line, 11.0)), 0.0, 0.0), dropout);

  float barCenter = mix(u_linePosition, fract(u_time * 0.12), step(0.001, u_brightnessBars));
  float barWidth = max(0.002, u_lineWidth + u_brightnessBars * 0.05);
  float bar = smoothstep(barWidth, 0.0, abs(uv.y - barCenter)) * max(u_brightnessBars, u_lineWidth * 0.25);
  yiq.x = yiq.x * u_gain + u_dcOffset + bar + u_whiteout;
  vec3 rgb = yiqToRgb(yiq);
  float clipAmount = clamp(u_clipping, 0.0, 0.98);
  rgb = clamp((rgb - clipAmount * 0.5) / max(0.02, 1.0 - clipAmount), 0.0, 1.0);
  vec3 solar = 1.0 - abs(rgb * 2.0 - 1.0);
  rgb = mix(rgb, solar, u_solarize);
  if (u_composite == 0) rgb = mix(rgb, feedback, clamp(u_feedbackAmount, 0.0, 0.975));
  out_color = vec4(clamp(rgb, 0.0, 1.0), 1.0);
}`;

const PRESENT_SHADER = `#version 300 es
precision highp float;
in vec2 v_uv;
out vec4 out_color;
uniform sampler2D u_texture;
uniform vec2 u_sourceSize;
uniform vec2 u_outputSize;
uniform int u_fit;
uniform int u_directSource;
vec2 mapUv(vec2 uv) {
  float sourceAspect = u_sourceSize.x / max(1.0, u_sourceSize.y);
  float outputAspect = u_outputSize.x / max(1.0, u_outputSize.y);
  vec2 p = uv - 0.5;
  if (u_fit == 1) {
    if (sourceAspect > outputAspect) p.y *= sourceAspect / outputAspect;
    else p.x *= outputAspect / sourceAspect;
  } else {
    if (sourceAspect > outputAspect) p.x *= outputAspect / sourceAspect;
    else p.y *= sourceAspect / outputAspect;
  }
  return p + 0.5;
}
void main() {
  vec2 uv = u_directSource == 1 ? mapUv(v_uv) : v_uv;
  if (any(lessThan(uv, vec2(0.0))) || any(greaterThan(uv, vec2(1.0)))) out_color = vec4(0.0, 0.0, 0.0, 1.0);
  else out_color = texture(u_texture, uv);
}`;

export interface RenderFrameOptions {
  now: number;
  source: SourceFrame;
  fit: "fit" | "fill";
  mode: RenderMode;
  video: Readonly<VideoParameters>;
  pads: Readonly<PadSnapshot>;
  modulation: Readonly<ModulationSnapshot>;
  bypass: boolean;
}

export interface WebGLRendererCallbacks {
  onContextLost?: () => void;
  onContextRestored?: () => void;
  onError?: (error: Error) => void;
}

interface Resources {
  effectProgram: WebGLProgram;
  presentProgram: WebGLProgram;
  vao: WebGLVertexArrayObject;
  sourceTexture: WebGLTexture;
  targets: [WebGLTexture, WebGLTexture];
  freezeTexture: WebGLTexture;
  framebuffer: WebGLFramebuffer;
}

function compile(gl: WebGL2RenderingContext, kind: number, source: string): WebGLShader {
  const shader = gl.createShader(kind);
  if (!shader) throw new Error("Unable to create WebGL shader");
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || "Unknown WebGL shader error";
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

function program(gl: WebGL2RenderingContext, fragmentSource: string): WebGLProgram {
  const vertex = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
  const fragment = compile(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const result = gl.createProgram();
  if (!result) throw new Error("Unable to create WebGL program");
  gl.attachShader(result, vertex);
  gl.attachShader(result, fragment);
  gl.linkProgram(result);
  gl.deleteShader(vertex);
  gl.deleteShader(fragment);
  if (!gl.getProgramParameter(result, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(result) || "Unknown WebGL link error";
    gl.deleteProgram(result);
    throw new Error(message);
  }
  return result;
}

function texture(gl: WebGL2RenderingContext, width: number, height: number): WebGLTexture {
  const result = gl.createTexture();
  if (!result) throw new Error("Unable to create WebGL texture");
  gl.bindTexture(gl.TEXTURE_2D, result);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  return result;
}

export class WebGLRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly width: number;
  readonly height: number;

  #gl: WebGL2RenderingContext;
  #resources?: Resources;
  #callbacks: WebGLRendererCallbacks;
  #contextLost = false;
  #sourceWidth = 1;
  #sourceHeight = 1;
  #sourceAllocated = false;
  #readTarget = 0;
  #lastOutput = 0;
  #freezeActive = false;
  #frame = 0;
  #onLost: (event: Event) => void;
  #onRestored: () => void;

  constructor(canvas: HTMLCanvasElement, width = 640, height = 480, callbacks: WebGLRendererCallbacks = {}) {
    this.canvas = canvas;
    this.width = width;
    this.height = height;
    canvas.width = width;
    canvas.height = height;
    const gl = canvas.getContext("webgl2", {
      alpha: false,
      antialias: false,
      depth: false,
      stencil: false,
      preserveDrawingBuffer: false,
      powerPreference: "high-performance",
    });
    if (!gl) throw new Error("WebGL2 is required to run CRTVAR");
    this.#gl = gl;
    this.#callbacks = callbacks;
    this.#onLost = (event) => {
      event.preventDefault();
      this.#contextLost = true;
      this.#resources = undefined;
      this.#callbacks.onContextLost?.();
    };
    this.#onRestored = () => {
      try {
        this.#contextLost = false;
        this.#initialize();
        this.#callbacks.onContextRestored?.();
      } catch (error) {
        this.#callbacks.onError?.(error instanceof Error ? error : new Error(String(error)));
      }
    };
    canvas.addEventListener("webglcontextlost", this.#onLost);
    canvas.addEventListener("webglcontextrestored", this.#onRestored);
    this.#initialize();
  }

  get contextLost(): boolean {
    return this.#contextLost;
  }

  get frame(): number {
    return this.#frame;
  }

  #initialize(): void {
    const gl = this.#gl;
    const effectProgram = program(gl, EFFECT_SHADER);
    const presentProgram = program(gl, PRESENT_SHADER);
    const vao = gl.createVertexArray();
    const framebuffer = gl.createFramebuffer();
    if (!vao || !framebuffer) throw new Error("Unable to allocate WebGL render resources");
    this.#resources = {
      effectProgram,
      presentProgram,
      vao,
      framebuffer,
      sourceTexture: texture(gl, 1, 1),
      targets: [texture(gl, this.width, this.height), texture(gl, this.width, this.height)],
      freezeTexture: texture(gl, this.width, this.height),
    };
    this.#sourceAllocated = false;
    this.#readTarget = 0;
    this.#lastOutput = 0;
    this.#freezeActive = false;
    gl.bindVertexArray(vao);
    gl.disable(gl.BLEND);
    gl.disable(gl.DEPTH_TEST);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    for (const target of this.#resources.targets) this.#clearTexture(target);
    this.#clearTexture(this.#resources.freezeTexture);
  }

  #clearTexture(target: WebGLTexture): void {
    const gl = this.#gl;
    const framebuffer = this.#resources!.framebuffer;
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, target, 0);
    gl.viewport(0, 0, this.width, this.height);
    gl.clearColor(0, 0, 0, 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
  }

  render(options: RenderFrameOptions): void {
    if (this.#contextLost || !options.source.ready) return;
    const resources = this.#resources;
    if (!resources) return;
    const gl = this.#gl;
    this.#uploadSource(options.source);

    const writeTarget = 1 - this.#readTarget;
    gl.bindFramebuffer(gl.FRAMEBUFFER, resources.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, resources.targets[writeTarget], 0);
    gl.viewport(0, 0, this.width, this.height);
    gl.useProgram(resources.effectProgram);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resources.sourceTexture);
    this.#uniform1i(resources.effectProgram, "u_source", 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, resources.targets[this.#readTarget]);
    this.#uniform1i(resources.effectProgram, "u_feedback", 1);
    this.#setEffectUniforms(resources.effectProgram, options);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.#lastOutput = writeTarget;
    this.#readTarget = writeTarget;

    if (options.pads.freeze && !this.#freezeActive) {
      gl.bindTexture(gl.TEXTURE_2D, resources.freezeTexture);
      gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, this.width, this.height);
    }
    this.#freezeActive = options.pads.freeze;

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, this.width, this.height);
    gl.useProgram(resources.presentProgram);
    gl.activeTexture(gl.TEXTURE0);
    const presented = options.bypass
      ? resources.sourceTexture
      : options.pads.freeze
        ? resources.freezeTexture
        : resources.targets[this.#lastOutput];
    gl.bindTexture(gl.TEXTURE_2D, presented);
    this.#uniform1i(resources.presentProgram, "u_texture", 0);
    this.#uniform2f(resources.presentProgram, "u_sourceSize", options.bypass ? this.#sourceWidth : this.width, options.bypass ? this.#sourceHeight : this.height);
    this.#uniform2f(resources.presentProgram, "u_outputSize", this.width, this.height);
    this.#uniform1i(resources.presentProgram, "u_fit", options.fit === "fit" ? 1 : 0);
    this.#uniform1i(resources.presentProgram, "u_directSource", options.bypass ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    this.#frame += 1;
  }

  #uploadSource(frame: SourceFrame): void {
    const gl = this.#gl;
    const resources = this.#resources!;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, resources.sourceTexture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    if (!this.#sourceAllocated || frame.width !== this.#sourceWidth || frame.height !== this.#sourceHeight) {
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, frame.source);
      this.#sourceAllocated = true;
      this.#sourceWidth = frame.width;
      this.#sourceHeight = frame.height;
    } else {
      gl.texSubImage2D(gl.TEXTURE_2D, 0, 0, 0, gl.RGBA, gl.UNSIGNED_BYTE, frame.source);
    }
  }

  #setEffectUniforms(program: WebGLProgram, options: RenderFrameOptions): void {
    const video = options.video;
    const pads = options.pads;
    this.#uniform2f(program, "u_sourceSize", this.#sourceWidth, this.#sourceHeight);
    this.#uniform2f(program, "u_outputSize", this.width, this.height);
    this.#uniform1f(program, "u_time", options.now / 1000);
    this.#uniform1f(program, "u_frame", this.#frame);
    this.#uniform1i(program, "u_fit", options.fit === "fit" ? 1 : 0);
    this.#uniform1i(program, "u_composite", options.mode === "composite" ? 1 : 0);
    this.#uniform1f(program, "u_horizontalTear", video.horizontalTear);
    this.#uniform1f(program, "u_verticalRoll", video.verticalRoll);
    this.#uniform1f(program, "u_lineShift", video.lineShift);
    this.#uniform1f(program, "u_dropout", video.dropout);
    this.#uniform1f(program, "u_chromaDisplacement", video.chromaDisplacement);
    this.#uniform1f(program, "u_chromaPhase", video.chromaPhase);
    this.#uniform1f(program, "u_chromaGain", video.chromaGain);
    this.#uniform1f(program, "u_desaturation", video.desaturation);
    this.#uniform1f(program, "u_snow", video.snow);
    this.#uniform1f(program, "u_impulses", video.impulses);
    this.#uniform1f(program, "u_brightnessBars", video.brightnessBars);
    this.#uniform1f(program, "u_clipping", video.clipping);
    this.#uniform1f(program, "u_solarize", video.solarize);
    this.#uniform1f(program, "u_audioReactive", video.audioReactive);
    this.#uniform1f(program, "u_feedbackAmount", video.feedback);
    this.#uniform1f(program, "u_syncDistortion", video.syncDistortion);
    this.#uniform1f(program, "u_burstDistortion", video.burstDistortion);
    this.#uniform1f(program, "u_burstGain", video.burstGain);
    this.#uniform1f(program, "u_linePosition", video.linePosition);
    this.#uniform1f(program, "u_lineWidth", video.lineWidth);
    this.#uniform1f(program, "u_gain", video.gain);
    this.#uniform1f(program, "u_dcOffset", video.dcOffset);
    this.#uniform1f(program, "u_audioRms", options.modulation.audioRms);
    this.#uniform1f(program, "u_audioPeak", options.modulation.audioPeak);
    this.#uniform1f(program, "u_syncKick", pads.syncKick);
    this.#uniform1f(program, "u_burstKill", pads.burstKill);
    this.#uniform1f(program, "u_lineCrush", pads.lineCrush);
    this.#uniform1f(program, "u_frameTear", pads.frameTear);
    this.#uniform1f(program, "u_whiteout", pads.whiteout);
    this.#uniform1f(program, "u_unlock", pads.unlock);
  }

  #uniform1f(program: WebGLProgram, name: string, value: number): void {
    const location = this.#gl.getUniformLocation(program, name);
    if (location !== null) this.#gl.uniform1f(location, value);
  }

  #uniform1i(program: WebGLProgram, name: string, value: number): void {
    const location = this.#gl.getUniformLocation(program, name);
    if (location !== null) this.#gl.uniform1i(location, value);
  }

  #uniform2f(program: WebGLProgram, name: string, x: number, y: number): void {
    const location = this.#gl.getUniformLocation(program, name);
    if (location !== null) this.#gl.uniform2f(location, x, y);
  }

  readPixels(width = 80, height = 60): Uint8Array {
    if (this.#contextLost || !this.#resources) return new Uint8Array(width * height * 4);
    const gl = this.#gl;
    const result = new Uint8Array(width * height * 4);
    const x = Math.max(0, Math.floor((this.width - width) / 2));
    const y = Math.max(0, Math.floor((this.height - height) / 2));
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.#resources.framebuffer);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, this.#resources.targets[this.#lastOutput], 0);
    gl.readPixels(x, y, width, height, gl.RGBA, gl.UNSIGNED_BYTE, result);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return result;
  }

  resetTemporalState(): void {
    if (!this.#resources || this.#contextLost) return;
    for (const target of this.#resources.targets) this.#clearTexture(target);
    this.#clearTexture(this.#resources.freezeTexture);
    this.#readTarget = 0;
    this.#lastOutput = 0;
    this.#freezeActive = false;
  }

  dispose(): void {
    this.canvas.removeEventListener("webglcontextlost", this.#onLost);
    this.canvas.removeEventListener("webglcontextrestored", this.#onRestored);
    const resources = this.#resources;
    if (resources && !this.#contextLost) {
      const gl = this.#gl;
      gl.deleteProgram(resources.effectProgram);
      gl.deleteProgram(resources.presentProgram);
      gl.deleteVertexArray(resources.vao);
      gl.deleteTexture(resources.sourceTexture);
      gl.deleteTexture(resources.targets[0]);
      gl.deleteTexture(resources.targets[1]);
      gl.deleteTexture(resources.freezeTexture);
      gl.deleteFramebuffer(resources.framebuffer);
    }
    this.#resources = undefined;
  }
}
